// Layer entitlement hook for Sovereign MUSE.
// Fetches GET /api/layers and returns the layers array.
// Follows the same pattern as useKeepsakes / useSession.

import { useState, useEffect } from 'react';
import { getStoredToken } from './useAuth';

export interface LayerInfo {
  id: string;
  label: string;
  entitled: boolean;
  available: boolean;
}

function buildHeaders(): Record<string, string> {
  const token = getStoredToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Only include Authorization when token exists — an empty Bearer fails validation.
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function useLayers() {
  const [layers, setLayers] = useState<LayerInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getStoredToken()) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    fetch('/api/layers', { headers: buildHeaders(), signal: controller.signal })
      .then((res) => (res.ok ? res.json() : { layers: [] }))
      .then((data: { layers: LayerInfo[] }) => {
        setLayers(data.layers ?? []);
      })
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setLayers([]);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  return { layers, loading };
}
